import { useCallback, useMemo, useState } from 'react';

import { Save } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridColumnResizeParams,
  GridEventListener,
  GridRenderCellParams,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowsProp
} from '@mui/x-data-grid';

import { Creator } from '@/models/creator';

import {
  buildInitialRows,
  isAccountInvalid,
  isCreatorInvalid,
  isRowInvalid
} from '../utils/creatorImport.utils';

const tooltipSlotProps = {
  tooltip: {
    sx: {
      backgroundColor: '#ffffff',
      color: '#1a1a1a',
      border: '1px solid #d0d0d0',
      fontSize: 13,
      fontWeight: 400,
      padding: '8px 12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
    }
  },
  arrow: {
    sx: {
      color: '#d0d0d0',
      '&::before': {
        backgroundColor: '#ffffff',
        border: '1px solid #d0d0d0'
      }
    }
  }
};

const getCreatorTooltip = (row: GridRowsProp[number]): string => {
  if (!String(row.creator ?? '').trim()) return "Creator can't be empty.";
  return '';
};

const getAccountTooltip = (row: GridRowsProp[number]): string => {
  if (!String(row.account ?? '').trim()) return "Account can't be empty.";
  if (row.isDuplicateAccount) return 'Account is duplicated.';
  return "Account must contain a URL linking to the Creator's account on a social media platform.";
};

const renderInvalidCell = (
  params: GridRenderCellParams,
  isInvalid: (row: GridRowsProp[number]) => boolean,
  getTooltip: (row: GridRowsProp[number]) => string
) => {
  if (!isInvalid(params.row)) return <span>{params.value}</span>;
  return (
    <Tooltip
      title={getTooltip(params.row)}
      arrow
      placement="bottom"
      slotProps={tooltipSlotProps}
    >
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          minHeight: 52
        }}
      >
        {params.value ?? ''}
      </span>
    </Tooltip>
  );
};

export const useCreatorGrid = (creators: Creator.FileValidationCreator[]) => {
  const [rows, setRows] = useState<GridRowsProp>(() =>
    buildInitialRows(creators)
  );
  const initialDuplicateAccounts = creators
    .flatMap((c) => c.creatorWarnings?.duplicates ?? [])
    .map((a) => a.trim());

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowId[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});

  const hasInvalidRows = rows.some(isRowInvalid);

  const handleDeleteSelected = useCallback(() => {
    setRows((prev) =>
      prev.filter((row) => !rowSelectionModel.includes(row.id))
    );
    setRowSelectionModel([]);
  }, [rowSelectionModel]);

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    setColumnWidths((prev) => ({
      ...prev,
      [params.colDef.field]: params.width
    }));
  };

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      Object.assign(event, { defaultMuiPrevented: true });
    }
  };

  const processRowUpdate = useCallback(
    (newRow: GridRowModel) => {
      const account = String(newRow.account ?? '').trim();
      const isDuplicateAccount = initialDuplicateAccounts.includes(account);
      const updatedRow: GridRowModel = { ...newRow, isDuplicateAccount };
      setRows((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
      );
      return updatedRow;
    },
    [initialDuplicateAccounts]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'creator',
        headerName: 'Creator',
        minWidth: 200,
        flex: columnWidths.creator ? undefined : 2,
        width: columnWidths.creator,
        editable: true,
        filterable: false,
        disableColumnMenu: true,
        cellClassName: (params) =>
          isCreatorInvalid(params.row) ? 'unresolved-account-cell' : '',
        renderCell: (params) =>
          renderInvalidCell(params, isCreatorInvalid, getCreatorTooltip)
      },
      {
        field: 'account',
        headerName: 'Account',
        minWidth: 200,
        flex: columnWidths.account ? undefined : 1,
        width: columnWidths.account,
        editable: true,
        filterable: false,
        disableColumnMenu: true,
        cellClassName: (params) =>
          isAccountInvalid(params.row) ? 'unresolved-account-cell' : '',
        renderCell: (params) =>
          renderInvalidCell(params, isAccountInvalid, getAccountTooltip)
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '',
        width: 40,
        getActions: ({ id }) =>
          rowModesModel[id]?.mode === GridRowModes.Edit
            ? [
                <GridActionsCellItem
                  key={String(id)}
                  icon={<Save fontSize="small" />}
                  label="Save"
                  title="Save row"
                  onClick={() =>
                    setRowModesModel((prev) => ({
                      ...prev,
                      [id]: { mode: GridRowModes.View }
                    }))
                  }
                />
              ]
            : []
      }
    ],
    [rowModesModel]
  );

  return {
    rows,
    columns,
    rowSelectionModel,
    setRowSelectionModel,
    rowModesModel,
    setRowModesModel,
    hasInvalidRows,
    handleDeleteSelected,
    handleRowEditStop,
    handleColumnWidthChange,
    processRowUpdate
  };
};
